import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@shared/ui/Button'
import { classifyAuthError } from '@shared/api/httpErrors'
import { useInvalidateProfile } from '@entities/user'
import { ROUTES } from '@shared/config/routes'
import { confirmLinkTelegram, type LinkTelegramConfirmResponse } from '../api/linkTelegramApi'
import styles from './LinkTelegramConfirmScreen.module.css'

type Status = 'pending' | 'success' | 'error'

const ERROR_MESSAGES: Record<string, string> = {
  token_not_found: 'Ссылка недействительна.',
  token_expired: 'Ссылка истекла. Попросите сгенерировать новую.',
  token_already_used: 'Ссылка уже использована.',
  user_already_has_telegram: 'Этот аккаунт уже связан с другим Telegram.',
  unauthorized: 'Откройте это приложение в Telegram.'
}

/**
 * Экран подтверждения связки Telegram с web-аккаунтом (TG-сборка).
 * Читает token из query и вызывает confirmLinkTelegram; отображает success/error-кейсы.
 */
export const LinkTelegramConfirmScreen = () => {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const invalidateProfile = useInvalidateProfile()
  const [status, setStatus] = useState<Status>('pending')
  const [result, setResult] = useState<LinkTelegramConfirmResponse | null>(null)
  const [errorText, setErrorText] = useState<string>('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorText('Нет токена связки')
      return
    }
    confirmLinkTelegram(token)
      .then(async (res) => {
        setResult(res)
        setStatus('success')
        await invalidateProfile()
      })
      .catch((err) => {
        const kind = classifyAuthError(err)
        setErrorText(ERROR_MESSAGES[kind] ?? 'Не удалось подтвердить связку.')
        setStatus('error')
      })
  }, [token, invalidateProfile])

  const goHome = () => navigate(ROUTES.HOME, { replace: true })

  if (status === 'pending') {
    return <div className={styles.pending}>Подтверждаем связку…</div>
  }

  if (status === 'error') {
    return (
      <div className={styles.screen}>
        <h1 className={styles.title}>Связка Telegram</h1>
        <div className={styles.error}>{errorText}</div>
        <Button onClick={goHome} block>В приложение</Button>
      </div>
    )
  }

  const r = result!
  let title = 'Ваш Telegram связан'
  let subtitle = r.email ? `Аккаунт: ${r.email}` : ''
  if (r.already_linked) {
    title = 'Уже связан'
    subtitle = r.email ? `Аккаунт: ${r.email}` : ''
  } else if (r.merged) {
    title = 'Аккаунты объединены'
    subtitle = r.email ? `Основной аккаунт: ${r.email}` : ''
  }

  return (
    <div className={styles.screen}>
      <h1 className={styles.title}>{title}</h1>
      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
      <Button onClick={goHome} block>В приложение</Button>
    </div>
  )
}
